import { useState, useCallback, useMemo } from 'react';
import { PipelineRunDto } from '../types/pipelineFlow';

export type ResultFilter = 'all' | 'succeeded' | 'failed' | 'canceled' | 'inProgress' | 'partiallySucceeded';
export type BranchFilter = 'all' | 'exclude-prs' | 'main-only' | 'prs-only';

export function usePipelineFilters(recentRuns: PipelineRunDto[]) {
  const [showFilters, setShowFilters] = useState(true);
  const [resultFilter, setResultFilter] = useState<string>('all');
  const [branchFilter, setBranchFilter] = useState<string>('all');
  const [requestedByFilter, setRequestedByFilter] = useState<string>('all');

  // Function to check if a run should be included based on branch filter
  const shouldIncludeRun = useCallback((sourceBranch?: string): boolean => {
    if (branchFilter === 'all') return true;
    if (branchFilter === 'exclude-prs') {
      return !sourceBranch?.toLowerCase().includes('/pull/');
    }
    if (branchFilter === 'main-only') {
      return sourceBranch?.toLowerCase().includes('main') || sourceBranch?.toLowerCase().includes('master') || false;
    }
    if (branchFilter === 'prs-only') {
      return sourceBranch?.toLowerCase().includes('/pull/') || false;
    }
    return true;
  }, [branchFilter]);

  // Filter recent runs based on current filter settings
  const filteredRuns = useMemo(() => {
    return recentRuns.filter((run: PipelineRunDto) => {
      // Result filter
      if (resultFilter !== 'all' && run.result.toLowerCase() !== resultFilter.toLowerCase()) {
        return false;
      }
      
      // Requested by filter
      if (requestedByFilter !== 'all' && !run.requestedFor.toLowerCase().includes(requestedByFilter.toLowerCase())) {
        return false;
      }
      
      // Branch filter
      if (!shouldIncludeRun(run.sourceBranch)) {
        return false;
      }
      
      return true;
    });
  }, [recentRuns, resultFilter, requestedByFilter, branchFilter, shouldIncludeRun]);

  // Get unique filter values for dropdowns
  const uniqueResults = useMemo(() => 
    [...new Set(recentRuns.map((r: PipelineRunDto) => r.result).filter(Boolean))].sort() as string[],
    [recentRuns]
  );
  const uniqueRequesters = useMemo(() =>
    [...new Set(recentRuns.map((r: PipelineRunDto) => r.requestedFor).filter(Boolean))].sort() as string[],
    [recentRuns]
  );

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    setResultFilter('all');
    setBranchFilter('all');
    setRequestedByFilter('all');
  }, []);

  // Check if filters are active
  const hasActiveFilters = useMemo(() => 
    resultFilter !== 'all' || branchFilter !== 'all' || requestedByFilter !== 'all',
    [resultFilter, branchFilter, requestedByFilter]
  );

  return {
    // State
    showFilters,
    resultFilter,
    branchFilter,
    requestedByFilter,
    
    // Derived state
    filteredRuns,
    uniqueResults,
    uniqueRequesters,
    hasActiveFilters,
    
    // Actions
    setShowFilters,
    setResultFilter,
    setBranchFilter,
    setRequestedByFilter,
    clearAllFilters,
  };
}